import React from "react";
import { createPortal } from "react-dom";

import { Icon } from "./icons";

const GAP = 8;
const EDGE = 8;

/** The bubble itself, at the end of <body> with fixed positioning so no
 * card, table or scroller clips it; above the anchor unless there is no
 * room, and kept inside the viewport. */
function Bubble({
  anchor,
  side,
  children,
}: {
  anchor: HTMLElement;
  side: "top" | "bottom";
  children: React.ReactNode;
}) {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [pos, setPos] = React.useState<React.CSSProperties>({
    visibility: "hidden",
  });
  React.useLayoutEffect(() => {
    const a = anchor.getBoundingClientRect();
    const b = ref.current?.getBoundingClientRect();
    if (!b) return;
    const below = side === "bottom" || a.top < b.height + GAP + EDGE;
    setPos({
      top: below ? a.bottom + GAP : a.top - GAP - b.height,
      left: Math.min(
        Math.max(EDGE, a.left + a.width / 2 - b.width / 2),
        window.innerWidth - b.width - EDGE,
      ),
    });
  }, [anchor, side, children]);
  return createPortal(
    <span ref={ref} className="ds-tip" aria-hidden style={pos}>
      {children}
    </span>,
    document.body,
  );
}

/** Hover and focus state for a tip anchor; scrolling or Escape hides it. */
function useTip(show: boolean) {
  const host = React.useRef<HTMLElement | null>(null);
  const [over, setOver] = React.useState(false);
  React.useEffect(() => {
    if (!over) return;
    const hide = () => setOver(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && hide();
    window.addEventListener("scroll", hide, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [over]);
  return {
    host,
    open: over || show,
    on: {
      onMouseEnter: () => setOver(true),
      onMouseLeave: () => setOver(false),
      onFocus: () => setOver(true),
      onBlur: () => setOver(false),
    },
  };
}

/** Hover/focus bubble over ``children``. Use for explanations; truncated
 * text keeps the native ``title``. `show` holds it open, e.g. to confirm a
 * copy. */
export function Tooltip({
  text,
  children,
  side = "top",
  show = false,
  focusable = false,
}: {
  text: React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "bottom";
  show?: boolean;
  focusable?: boolean;
}) {
  const id = React.useId();
  const tip = useTip(show);
  return (
    <span
      ref={tip.host as React.RefObject<HTMLSpanElement>}
      className="ds-tip-host"
      tabIndex={focusable ? 0 : undefined}
      aria-describedby={id}
      {...tip.on}
    >
      {children}
      <span role="tooltip" id={id} className="ds-sr">
        {text}
      </span>
      {tip.open && tip.host.current && (
        <Bubble anchor={tip.host.current} side={side}>
          {text}
        </Bubble>
      )}
    </span>
  );
}

/** Set by clickable parents (a tile that is itself a button), where a
 * nested button would be invalid; the dot then renders as a labelled image. */
export const InsideControl = React.createContext(false);

/** The (i) beside a title; its text shows on hover and on keyboard focus. */
export function InfoDot({ text }: { text: string }) {
  const inside = React.useContext(InsideControl);
  const tip = useTip(false);
  const bubble = tip.open && tip.host.current && (
    <Bubble anchor={tip.host.current} side="top">
      {text}
    </Bubble>
  );
  return inside ? (
    <span
      ref={tip.host as React.RefObject<HTMLSpanElement>}
      className="ds-tip-host ds-info"
      role="img"
      aria-label={text}
      {...tip.on}
    >
      <Icon name="info" size={14} />
      {bubble}
    </span>
  ) : (
    <button
      ref={tip.host as React.RefObject<HTMLButtonElement>}
      type="button"
      className="ds-tip-host ds-info"
      aria-label={text}
      {...tip.on}
    >
      <Icon name="info" size={14} />
      {bubble}
    </button>
  );
}
